import type {
	AIContextServicePanelApiResponse,
	AIContextServicePanelResourceResponse,
	DraftReplyNextStepInlineAction,
	RawSuggestedNextStep,
} from './ContextPanelServiceTypes.tsx';

export const transformContent = (content: {
	title: string;
	description: string;
}): {
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	title: any;
	// oxlint-disable-next-line @typescript-eslint/no-explicit-any
	description: any;
} => {
	return {
		title: parseAdf(content.title),
		description: parseAdf(content.description),
	};
};

const normaliseDraftReplyInlineActionFromApi = (
	inlineAction: RawSuggestedNextStep['inlineAction'] | undefined,
): DraftReplyNextStepInlineAction | undefined => {
	if (!inlineAction || inlineAction.type !== 'DRAFT_REPLY' || !inlineAction.draftReplyPill) {
		return undefined;
	}
	const p = inlineAction.draftReplyPill;
	const text = p.text != null ? String(p.text).trim() : '';
	const category = p.category != null ? String(p.category).trim() : '';
	// Execution only requires text + category; pillId is schema metadata (may be absent on REST).
	if (text === '' || category === '') {
		return undefined;
	}
	const pillId = p.pillId != null ? String(p.pillId).trim() : '';
	return {
		type: 'DRAFT_REPLY',
		draftReplyPill: {
			pillId,
			text,
			category,
			...(p.intent != null && p.intent !== '' ? { intent: p.intent } : {}),
			...(p.goal != null && p.goal !== '' ? { goal: p.goal } : {}),
			...(p.triggerSummary != null && p.triggerSummary !== ''
				? { triggerSummary: p.triggerSummary }
				: {}),
			...(p.example != null && p.example !== '' ? { example: p.example } : {}),
		},
	};
};

const transformSuggestedNextSteps = (
	suggestedNextSteps: AIContextServicePanelApiResponse['suggestedNextSteps'],
): AIContextServicePanelResourceResponse['suggestedNextSteps'] => {
	if (!suggestedNextSteps?.citations?.length && !suggestedNextSteps?.nextSteps?.length) {
		return undefined;
	}
	return {
		citations: (suggestedNextSteps?.citations ?? []).map((c) => ({
			id: c.id,
			sourceType: c.sourceType ?? '',
			sourceId: c.sourceId ?? null,
			sourceUrl: c.sourceUrl,
			title: c.title,
		})),
		nextSteps: (suggestedNextSteps?.nextSteps ?? []).flatMap((step) => {
			const content = parseAdf(step?.content);
			if (content == null) {
				return [];
			}
			const normalizedInlineAction = normaliseDraftReplyInlineActionFromApi(step?.inlineAction);
			return [
				{
					content,
					...(normalizedInlineAction != null ? { inlineAction: normalizedInlineAction } : {}),
				},
			];
		}),
	};
};

export const transformData = (
	data: AIContextServicePanelApiResponse,
): AIContextServicePanelResourceResponse => ({
	type: 'ServicePanelSuggestions',
	summary: parseAdf(data.summary),
	reporterDetails: parseAdf(data.reporterDetails),
	agentSteps: parseAdf(data.agentSteps),
	suggestedNextSteps: transformSuggestedNextSteps(data.suggestedNextSteps),
	suggestedActions: data.suggestedActions
		? data.suggestedActions.map((action) => {
				switch (action.type) {
					case 'priority':
						return {
							type: action.type,
							context: {
								...action.context,
								suggestion: action.context.suggestion,
							},
							content: transformContent(action.content),
						};
					case 'comment':
						return {
							type: action.type,
							context: {
								...action.context,
								suggestion: action.context.suggestion,
							},
							content: transformContent(action.content),
						};
					case 'assignee':
					default:
						return {
							type: action.type,
							context: {
								...action.context,
								suggestion: action.context.suggestion,
							},
							content: transformContent(action.content),
						};
				}
			})
		: [],
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const parseAdf = (adfString: string | null | undefined): any => {
	if (!adfString) return null;

	try {
		return JSON.parse(adfString);
	} catch (_error) {
		return null;
	}
};
